import KpiCard from '@/components/dashboard/KpiCard';
import ChartCard from '@/components/dashboard/ChartCard';
import ExecutiveConsole from '@/components/dashboard/ExecutiveConsole';
import useDashboardV2 from '@/hooks/useDashboardV2';
import { Briefcase, TrendingUp, DollarSign, Target, Award, Users, Building2, Car, GraduationCap } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, Cell } from 'recharts';

// Configuración tropicalizada por vertical
const VERTICAL_CONFIG = {
  real_estate: {
    title: 'Salud del negocio inmobiliario',
    extraIcon: Building2,
    extraLabel: 'Propiedades activas',
    extraKey: 'active_properties',
    extraColor: 'sky',
  },
  developments: {
    title: 'Salud del negocio de desarrollos',
    extraIcon: Building2,
    extraLabel: 'Desarrollos activos',
    extraKey: 'active_developments',
    extraColor: 'violet',
  },
  automotive: {
    title: 'Salud del negocio automotriz',
    extraIcon: Car,
    extraLabel: 'Catálogo de modelos',
    extraKey: 'total_car_models',
    extraColor: 'violet',
  },
  education: {
    title: 'Salud del negocio educativo',
    extraIcon: GraduationCap,
    extraLabel: 'Inscritos',
    extraKey: 'enrolled_count',
    extraColor: 'emerald',
  },
  generic: {
    title: 'Salud del negocio',
    extraIcon: Briefcase,
    extraLabel: '—',
    extraKey: null,
    extraColor: 'cyan',
  },
};

/**
 * Dashboard de Dirección — vista ejecutiva con KPIs agregados,
 * tendencias y comparativas. Tropicalizado por vertical.
 */
export default function DireccionTab({ filters, vertical = 'generic' }) {
  const cfg = VERTICAL_CONFIG[vertical] || VERTICAL_CONFIG.generic;
  const ExtraIcon = cfg.extraIcon;

  const kpisQ = useDashboardV2({ endpoint: 'kpis', filters, vertical });
  const trendsQ = useDashboardV2({ endpoint: 'trends', filters, vertical, extra: { months_back: 12 } });
  const perfQ = useDashboardV2({ endpoint: 'vendor-performance', filters, vertical, extra: { limit: 5 } });

  const core = kpisQ.data?.core || {};
  const verticalKpis = kpisQ.data?.vertical_kpis || {};
  const monthly = trendsQ.data?.monthly || [];
  const topVendors = perfQ.data?.vendors || [];

  const extraValue = cfg.extraKey ? (verticalKpis[cfg.extraKey] ?? 0) : '—';

  return (
    <div className="space-y-6" data-testid="tab-direccion-content">
      {/* Asistente Ejecutivo IA -- restaurado a pedido del owner (jul2026), extendido
          para incluir las metricas nuevas de esta rama en su data brief */}
      <ExecutiveConsole />

      <div>
        <h3 className="text-lg font-heading text-white font-bold">{cfg.title}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Indicadores ejecutivos del período seleccionado</p>
      </div>

      <div className="flex overflow-x-auto gap-3 pb-1 sm:grid sm:grid-cols-3 lg:grid-cols-5">
        <KpiCard icon={Users} label="Leads totales" value={kpisQ.loading ? '—' : core.total_leads ?? 0} color="cyan" loading={kpisQ.loading} testid="dir-kpi-leads" />
        <KpiCard icon={Target} label="Conversión" value={kpisQ.loading ? '—' : `${core.conversion_rate ?? 0}%`} color="emerald" loading={kpisQ.loading} testid="dir-kpi-conv" />
        <KpiCard icon={Award} label="Ganados" value={kpisQ.loading ? '—' : core.won_leads ?? 0} color="amber" loading={kpisQ.loading} testid="dir-kpi-won" />
        <KpiCard icon={DollarSign} label="Valor pipeline" value={kpisQ.loading ? '—' : `$${((core.total_pipeline_value ?? 0) / 1000).toFixed(1)}k`} color="violet" loading={kpisQ.loading} testid="dir-kpi-value" />
        <KpiCard icon={ExtraIcon} label={cfg.extraLabel} value={kpisQ.loading ? '—' : extraValue} color={cfg.extraColor} loading={kpisQ.loading} testid="dir-kpi-extra" />
      </div>

      <ChartCard title="Tendencia anual (12 meses)" icon={TrendingUp} loading={trendsQ.loading} empty={!monthly.length} testid="dir-chart-trend" height={320} tabKey="direccion" chartId="dir-chart-trend">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={monthly} margin={{ left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="month" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
            <YAxis yAxisId="left" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Cantidad', angle: -90, position: 'insideLeft', offset: 10, style: { fill: '#8893A7', fontSize: 10 } }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} label={{ value: 'Valor ($)', angle: 90, position: 'insideRight', offset: 10, style: { fill: '#8893A7', fontSize: 10 } }} />
            <Tooltip contentStyle={{ background: '#1F2833', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Line yAxisId="left" type="monotone" dataKey="leads" stroke="#1B9CEA" strokeWidth={2} dot={{ r: 3 }} name="Leads" />
            <Line yAxisId="left" type="monotone" dataKey="ganados" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} name="Ganados" />
            <Line yAxisId="right" type="monotone" dataKey="valor" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} name="Valor pipeline" />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Top 5 vendedores por conversión" icon={Award} loading={perfQ.loading} empty={!topVendors.length} testid="dir-chart-top" tabKey="direccion" chartId="dir-chart-top">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={topVendors} layout="vertical" margin={{ left: 60, right: 12, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} tickFormatter={v => `${v}%`} label={{ value: 'Conversión (%)', position: 'insideBottom', offset: -5, style: { fill: '#8893A7', fontSize: 10 } }} />
              <YAxis dataKey="name" type="category" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} width={110} />
              <Tooltip contentStyle={{ background: '#1F2833', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 11 }} formatter={v => [`${v}%`, 'Conversión']} />
              <Bar dataKey="conversion_rate" radius={[0, 4, 4, 0]}>
                {topVendors.map((_, i) => <Cell key={i} fill={['#10B981', '#1B9CEA', '#A5D62C', '#F59E0B', '#8B5CF6'][i % 5]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Resumen ejecutivo" icon={Briefcase} loading={kpisQ.loading} testid="dir-chart-exec" tabKey="direccion" chartId="dir-chart-exec">
          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between p-2 rounded bg-black/20 border border-white/5">
              <span className="text-muted-foreground">Leads nuevos del período</span>
              <span className="text-white font-semibold">{core.new_leads ?? 0}</span>
            </div>
            <div className="flex items-center justify-between p-2 rounded bg-black/20 border border-white/5">
              <span className="text-muted-foreground">Leads perdidos</span>
              <span className="text-rose-400 font-semibold">{core.lost_leads ?? 0}</span>
            </div>
            <div className="flex items-center justify-between p-2 rounded bg-black/20 border border-white/5">
              <span className="text-muted-foreground">Pipeline activo</span>
              <span className="text-violet-300 font-semibold">{core.total_opportunities ?? 0}</span>
            </div>
            <div className="flex items-center justify-between p-2 rounded bg-black/20 border border-white/5">
              <span className="text-muted-foreground">Tasa de conversión</span>
              <span className="text-emerald-400 font-semibold">{core.conversion_rate ?? 0}%</span>
            </div>
            <div className="flex items-center justify-between p-2 rounded bg-black/20 border border-white/5">
              <span className="text-muted-foreground">Valor total del pipeline</span>
              <span className="text-amber-300 font-semibold">${(core.total_pipeline_value ?? 0).toLocaleString()}</span>
            </div>
          </div>
        </ChartCard>
      </div>
    </div>
  );
}
